import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, track, fileUrl, SPECIALTY_EXCLUDE_IDS } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useBooking } from "@/context/BookingContext";
import { Button } from "@/components/ui/button";
import Reveal from "@/components/Reveal";
import SEO from "@/components/SEO";
import { UtensilsCrossed, CalendarCheck, ArrowRight, Clock, Star, Quote, Camera, Sparkles } from "lucide-react";

const ANTIPASTI_IMG = "/api/files/masetti/uploads/b8787dd7-72f0-434e-910f-9e51a06547a3.jpg";
const PRIMI_IMG = "/api/files/masetti/uploads/9db1230d-da86-43f9-97aa-99ab872f707a.jpg";
const GRUPPI_IMG = "/api/files/masetti/uploads/ff6bbd0f-bed3-4165-8538-ce10693ac7f8.jpg";
const GRIGLIA_IMG = "/api/files/masetti/uploads/6960e3e5-f95f-4c8e-afd4-68048eb52c3e.jpg";

export default function Home() {
  const { settings } = useSettings();
  const { open: openBooking } = useBooking();
  const [dishes, setDishes] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [googleRating, setGoogleRating] = useState(null);
  const [carouselIdx, setCarouselIdx] = useState(0);
  const onCarouselScroll = (e) => {
    const el = e.currentTarget;
    if (el.clientWidth) setCarouselIdx(Math.round(el.scrollLeft / el.clientWidth));
  };

  useEffect(() => {
    track("page_view", "home");
    api.get("/dishes").then((r) => setDishes(r.data)).catch(() => {});
    api.get("/gallery").then((r) => setGallery(r.data)).catch(() => {});
    api.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    api.get("/google-rating").then((r) => setGoogleRating(r.data)).catch(() => {});
  }, []);

  const familyPhoto = gallery.find((g) => g.category === "famiglia");
  const familyImg = settings.family_image ? fileUrl(settings.family_image) : (familyPhoto ? fileUrl(familyPhoto.image) : "https://images.unsplash.com/photo-1622322781846-37e0262fa0e0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000");

  const featured = [
    { key: "antipasti", nome: settings.home_antipasti_title || "Antipasti tradizionali", desc: settings.home_antipasti_desc || "Crescentine e gnocco fritto caldi con i nostri salumi e la mortadella.", img: fileUrl(settings.home_antipasti_image || ANTIPASTI_IMG), alt: settings.home_antipasti_title || "Antipasti tradizionali: crescentine, salumi e mortadella" },
    { key: "primi", nome: settings.home_primi_title || "Primi della casa", desc: settings.home_primi_desc || "Pasta fresca tirata a mano ogni giorno, dal ragù alla pasta ripiena burro e salvia.", img: fileUrl(settings.home_primi_image || PRIMI_IMG), alt: settings.home_primi_title || "Primi della casa" },
    { key: "griglia", nome: settings.home_griglia_title || "La nostra griglia", desc: settings.home_griglia_desc || "Carni cotte sulla brace: salsiccia, tagliata, costine di maiale e di castrato, pollo e fegato di maiale.", img: fileUrl(settings.home_griglia_image || GRIGLIA_IMG), alt: settings.home_griglia_title || "La nostra griglia: carni miste sulla brace" },
  ];

  const mobileDishes = [
    ...dishes.filter((d) => d.home_evidenza && d.image && d.available !== false),
    ...dishes
      .filter((d) => d.specialita && d.image && !d.home_evidenza && !SPECIALTY_EXCLUDE_IDS.includes(d.id))
      .sort((a, b) => (a.specialita_order ?? 999) - (b.specialita_order ?? 999)),
  ];

  const gReviews = (googleRating?.reviews || [])
    .filter((r) => (r.rating || 0) >= 4 && ((r.text || "").trim().length >= 40))
    .map((r, i) => {
      const raw = (r.text || "").replace(/\s+/g, " ").trim();
      const testo = raw.length > 180 ? raw.slice(0, 180).trimEnd() + "…" : raw;
      return { id: `g-${i}`, autore: r.author, testo, rating: r.rating, fonte: "Google" };
    });
  const reviews = (testimonials.length > 0 ? testimonials : gReviews).slice(0, 3);

  return (
    <div>
      <SEO />

      {settings.banner_active && settings.banner_text && (!settings.banner_until || new Date(settings.banner_until + "T23:59:59") >= new Date()) && (
        <div className="bg-bordeaux text-cream" data-testid="home-season-banner">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center text-sm">
            <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-brass shrink-0" /><span className="font-accent italic text-base">{settings.banner_text}</span></span>
            {settings.banner_link && (
              <Link to={settings.banner_link} onClick={() => track("cta_click", "home-banner")} className="inline-flex items-center gap-1 font-medium text-brass underline underline-offset-4 decoration-brass/60 hover:text-cream transition-colors" data-testid="home-season-banner-link">
                {settings.banner_cta || "Scopri"} <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* 1. HERO */}
      <section className="relative h-[85vh] min-h-[520px] flex items-end">
        {settings.hero_image && <img src={fileUrl(settings.hero_image)} alt={settings.hero_alt || "Ristorante Masetti"} className="absolute inset-0 w-full h-full object-cover" />}
        <div className="absolute inset-0 hero-overlay" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 sm:pb-24 w-full fade-up">
          <span className="inline-block font-accent italic text-brass text-lg sm:text-xl mb-2 drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]">Dal 1961 a Zola Predosa</span>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold text-cream tracking-tight leading-tight drop-shadow-[0_2px_6px_rgba(0,0,0,0.55)]">RISTORANTE MASETTI</h1>
          <p className="text-cream text-lg sm:text-xl mt-3 drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]">Cucina bolognese, da tre generazioni</p>
          <p className="font-accent italic text-brass text-xl sm:text-2xl mt-1 drop-shadow-[0_1px_4px_rgba(0,0,0,0.7)]">“{settings.tagline || "La cucina bolognese, per stare insieme."}”</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <Button onClick={() => openBooking("hero")} className="bg-brass hover:bg-brass-hover text-oak-dark rounded-full px-7 h-12 text-base shadow-md" data-testid="hero-prenota-btn"><CalendarCheck className="w-4 h-4 mr-2" />Prenota un tavolo</Button>
            <Link to="/menu" onClick={() => track("menu_view", "hero")}>
              <Button variant="outline" className="border-cream/70 text-cream bg-transparent hover:bg-cream/10 rounded-full px-7 h-12 text-base" data-testid="hero-menu-btn"><UtensilsCrossed className="w-4 h-4 mr-2" />Scopri il menù</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 2. PIATTI DELLA CUCINA */}
      <section className="bg-cream-dark py-14 sm:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Reveal direction="up">
            <div className="text-center mb-5 md:mb-10">
              <p className="font-accent italic text-brass-hover text-lg">Le nostre specialità</p>
              <h2 className="font-serif text-3xl sm:text-4xl text-bordeaux">Piatti della cucina</h2>
            </div>
          </Reveal>
          {mobileDishes.length > 0 && (
            <div onScroll={onCarouselScroll} className="md:hidden -mx-4 flex overflow-x-auto snap-x snap-mandatory [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-testid="home-dish-carousel">
              {mobileDishes.map((d) => (
                <div key={d.id} className="snap-center shrink-0 w-full px-4">
                  <figure className="relative aspect-[4/5] rounded-2xl overflow-hidden shadow-md" data-testid={`home-carousel-dish-${d.id}`}>
                    <Link to={`/menu?piatto=${d.id}`} onClick={() => track("menu_view", "home-carousel")} className="absolute inset-0" aria-label={`Vedi ${d.nome} nel menù`} data-testid={`home-carousel-link-${d.id}`}>
                      <img src={fileUrl(d.image)} alt={d.image_alt || d.nome} loading="lazy" className="w-full h-full object-cover object-center" />
                    </Link>
                    <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/80 via-black/40 to-transparent pointer-events-none" />
                    <figcaption className="absolute inset-x-0 bottom-0 p-5 text-cream pointer-events-none [&_a]:pointer-events-auto">
                      <h3 className="font-serif text-2xl leading-tight drop-shadow">{d.nome}</h3>
                      {d.descrizione && <p className="font-accent italic text-cream/90 mt-1 leading-snug drop-shadow">{d.descrizione}</p>}
                      <Link to="/galleria#cucina" onClick={() => track("cta_click", "home-carousel-cucina")} className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-brass underline underline-offset-4 decoration-brass/60 hover:text-cream transition-colors" data-testid={`home-carousel-cucina-${d.id}`}>
                        <Camera className="w-4 h-4" />Scopri la nostra cucina
                      </Link>
                    </figcaption>
                  </figure>
                </div>
              ))}
            </div>
          )}
          {mobileDishes.length > 1 && (
            <div className="md:hidden flex justify-center gap-2 mt-4" data-testid="home-dish-carousel-dots">
              {mobileDishes.map((d, i) => (
                <span key={d.id} className={`h-1.5 rounded-full transition-all duration-300 ${i === carouselIdx ? "w-5 bg-bordeaux" : "w-1.5 bg-brass/40"}`} />
              ))}
            </div>
          )}
          <div className={`grid-cols-1 md:grid-cols-3 gap-5 sm:gap-6 ${mobileDishes.length > 0 ? "hidden md:grid" : "grid"}`}>
            {featured.map((f, i) => (
              <Reveal key={f.key} direction="up" delay={i * 120}>
                <figure className="group bg-white rounded-2xl border border-brass/15 overflow-hidden h-full flex flex-col shadow-sm" data-testid={`home-dish-${f.key}`}>
                  <div className="h-56 sm:h-60 overflow-hidden">
                    <img src={f.img} alt={f.alt} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  </div>
                  <figcaption className="p-5 flex-1 flex flex-col">
                    <h3 className="font-serif text-xl text-bordeaux">{f.nome}</h3>
                    <p className="font-accent italic text-oak/80 mt-1.5 leading-snug">{f.desc}</p>
                  </figcaption>
                </figure>
              </Reveal>
            ))}
          </div>
          <div className="flex flex-wrap justify-center gap-3 mt-10">
            <Link to="/menu" onClick={() => track("menu_view", "home-cta")}>
              <Button className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-8 h-12" data-testid="home-menu-cta">Scopri il nostro menù <ArrowRight className="w-4 h-4 ml-2" /></Button>
            </Link>
            <Link to="/galleria#cucina" onClick={() => track("cta_click", "home-cucina")} className="hidden md:inline-flex">
              <Button variant="outline" className="border-bordeaux/40 text-bordeaux bg-transparent hover:bg-bordeaux hover:text-cream rounded-full px-8 h-12" data-testid="home-cucina-cta"><Camera className="w-4 h-4 mr-2" />Scopri la nostra cucina</Button>
            </Link>
          </div>
        </div>
      </section>

      {/* 3. A TAVOLA, INSIEME */}
      <section className="py-14 sm:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
          <Reveal direction="left">
            <img src={fileUrl(GRUPPI_IMG)} alt="La veranda del Ristorante Masetti apparecchiata per gruppi ed eventi" loading="lazy" className="rounded-2xl w-full h-64 md:h-80 object-cover shadow-md" />
          </Reveal>
          <Reveal direction="right">
            <h2 className="font-serif text-3xl sm:text-4xl text-bordeaux">A tavola, insieme.</h2>
            <p className="font-accent italic text-xl text-brass-hover mt-1">Cene aziendali, feste e pranzi di gruppo a Zola Predosa.</p>
            <p className="text-oak/80 mt-4 leading-relaxed">Dalle cene di lavoro alle ricorrenze, accogliamo i gruppi con proposte dedicate e la nostra cucina bolognese.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/aziende" onClick={() => track("cta_click", "home-gruppi")}><Button className="bg-bordeaux hover:bg-bordeaux-dark text-cream rounded-full px-6" data-testid="gruppi-proposte">Scopri le proposte per gruppi</Button></Link>
              <Link to="/contatti"><Button variant="outline" className="border-bordeaux/40 text-bordeaux bg-transparent hover:bg-bordeaux hover:text-cream rounded-full px-6" data-testid="gruppi-info">Chiedi informazioni</Button></Link>
              <Link to="/galleria#ambienti" onClick={() => track("cta_click", "home-ambienti")}><Button variant="outline" className="border-bordeaux/40 text-bordeaux bg-transparent hover:bg-bordeaux hover:text-cream rounded-full px-6" data-testid="gruppi-ambienti">Scopri gli ambienti</Button></Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 5. FAMIGLIA / STORIA */}
      <section className="bg-bordeaux text-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20 grid md:grid-cols-2 gap-10 items-center">
          <Reveal direction="left" className="order-2 md:order-1">
            <p className="font-accent italic text-brass text-lg">Una famiglia, una tavola</p>
            <h2 className="font-serif text-3xl sm:text-4xl mt-1">Da tre generazioni, a casa da Masetti</h2>
            <p className="text-cream/90 mt-4 leading-relaxed">Dal 1961 accogliamo gli ospiti a Zola Predosa con le ricette della nostra famiglia, la sfoglia tirata a mano e il piacere di stare a tavola insieme.</p>
            <div className="flex flex-wrap gap-3 mt-6">
              <Link to="/storia"><Button className="bg-brass hover:bg-brass-hover text-oak-dark rounded-full px-6" data-testid="family-storia">La nostra storia</Button></Link>
              <Link to="/galleria#storiche" onClick={() => track("cta_click", "home-storiche")}><Button variant="outline" className="border-cream/50 text-cream bg-transparent hover:bg-cream hover:text-bordeaux rounded-full px-6" data-testid="family-foto-storiche">Sfoglia le foto storiche</Button></Link>
            </div>
          </Reveal>
          <Reveal direction="right" className="order-1 md:order-2">
            <img src={familyImg} alt={settings.family_image_alt || familyPhoto?.image_alt || "La famiglia Masetti"} loading="lazy" className="rounded-2xl w-full h-72 md:h-96 object-cover shadow-lg" />
          </Reveal>
        </div>
      </section>

      {/* 6. RECENSIONI */}
      {(reviews.length > 0 || (googleRating && googleRating.rating)) && (
        <section className="bg-cream-dark py-12 sm:py-16">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <Reveal direction="up">
              <div className="text-center mb-8">
                <p className="font-accent italic text-brass-hover text-lg">Dicono di noi</p>
                <h2 className="font-serif text-3xl sm:text-4xl text-bordeaux">La parola ai nostri ospiti</h2>
                {googleRating && googleRating.rating && (
                  <a href={googleRating.reviews_uri || googleRating.maps_uri || "#"} target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-2 mt-4 bg-white border border-brass/25 rounded-full px-5 py-2 shadow-sm hover:border-brass transition-colors" data-testid="google-rating-badge">
                    <span className="flex gap-0.5">{[...Array(5)].map((_, i) => <Star key={i} className={`w-4 h-4 ${i < Math.round(googleRating.rating) ? "fill-brass text-brass" : "text-brass/30"}`} />)}</span>
                    <span className="text-sm text-oak"><b className="font-serif text-bordeaux">{googleRating.rating.toFixed(1).replace(".", ",")}</b> su 5 · {googleRating.count} recensioni su Google</span>
                  </a>
                )}
              </div>
            </Reveal>
            {reviews.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {reviews.map((t, i) => (
                  <Reveal key={t.id || `t-${i}`} direction="up" delay={i * 100}>
                    <figure className="bg-white rounded-2xl border border-brass/15 p-6 flex flex-col h-full" data-testid={`testimonial-${t.id}`}>
                      <Quote className="w-7 h-7 text-brass/40" />
                      <div className="flex gap-0.5 my-3">
                        {[...Array(Number(t.rating) || 5)].map((_, i) => <Star key={i} className="w-4 h-4 fill-brass text-brass" />)}
                      </div>
                      <blockquote className="font-accent italic text-lg text-oak/85 leading-relaxed flex-1">"{t.testo}"</blockquote>
                      <figcaption className="mt-4 text-sm text-oak/70">
                        <span className="font-semibold text-oak">{t.autore}</span>{t.fonte ? ` · ${t.fonte}` : ""}
                      </figcaption>
                    </figure>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* 7. VENITE A TROVARCI */}
      <section className="bg-bordeaux text-cream py-14">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Reveal direction="up">
            <Clock className="w-8 h-8 mx-auto text-brass mb-3" />
            <h2 className="font-serif text-3xl">Venite a trovarci</h2>
            <a href={settings.google_maps_url || `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${settings.address}, ${settings.city}`)}`} target="_blank" rel="noreferrer" className="inline-block hover:text-brass transition-colors" data-testid="venite-address-link">
              <p className="font-accent italic text-xl text-cream/90 mt-2">{settings.address}</p>
              <p className="font-accent italic text-lg text-cream/80">{settings.city}</p>
            </a>
            <div className="flex flex-wrap justify-center gap-3 mt-6">
              <Button onClick={() => openBooking("home-footer")} className="bg-brass hover:bg-brass-hover text-oak-dark rounded-full px-6" data-testid="home-footer-prenota">Prenota un tavolo</Button>
              <Link to="/contatti"><Button variant="outline" className="border-cream/60 text-cream bg-transparent hover:bg-cream/10 rounded-full px-6" data-testid="home-footer-contatti">Orari e contatti</Button></Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
